import React, { useMemo, useState } from 'react';
import {
  Table,
  Button,
  Pagination,
  Form,
  Row,
  Col,
  Badge,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { GoPrimitiveDot } from 'react-icons/go';
import { useTable, useSortBy, usePagination } from 'react-table';
import _ from 'lodash';
import { IconButton } from '@material-ui/core';
import AuthStorage from '~/Utils/AuthStorage';
import {
  obterTotalTitulosSinal,
  agruparPorContaFamiliaCorretor,
} from '../helpers';
import { formatarMonetario, format, salvarEmExcel } from '~/Utils/Helpers';
import { TabelaInadimplenciaContainer } from './styles';
import moment from 'moment';
import { FaSearch, FaFileExcel } from 'react-icons/fa';
import { Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';

const TabelaInadimplencia = ({
  registros,
  mostrarDetalhesRegistro,
  empreendimentos,
}) => {
  const registrosTabela = _(agruparPorContaFamiliaCorretor(registros))
    .orderBy(entry => entry.cliente.razao_social)
    .value();
  const isAdm = AuthStorage.isADM();
  const isCor = AuthStorage.isCorretor();

  const columns = useMemo(
    () => [
      {
        Header: 'Último Contato',
        accessor: 'ultimoContato',
        id: 'ultimoContato',
        sortType: 'basic',
      },
      {
        Header: 'Nome Cliente',
        accessor: 'nome',
        Cell: ({ row, value }) => (
          <span className="d-block">
            <span>{value}</span>
            {row.original.encaminhada && (
              <OverlayTrigger
                overlay={
                  <Tooltip>
                    Um ou mais contratos foram encaminhados para o Departamento
                    de Adm. de Recebíveis (Cobrança)
                  </Tooltip>
                }
              >
                <Badge
                  variant="warning"
                  style={{ cursor: 'default' }}
                  className="ml-2"
                >
                  Encaminhada
                </Badge>
              </OverlayTrigger>
            )}
            {row.original.teveContato && (
              <OverlayTrigger
                overlay={
                  <Tooltip>
                    Ocorrência registrada em um ou mais contratos
                  </Tooltip>
                }
              >
                <GoPrimitiveDot color="#007bff" className="ml-2" />
              </OverlayTrigger>
            )}
          </span>
        ),
      },
      {
        Header: 'CPF',
        accessor: 'cpf',
        Cell: props => format.cpf(props.value),
      },
      {
        Header: 'Empreendimento',
        accessor: 'nomeEmpreendimento',
      },
      {
        Header: 'Imobiliária',
        id: 'nomeImobiliaria',
        accessor: 'nomeImobiliaria',
      },
      {
        Header: 'Corretor',
        id: 'nomeCorretor',
        accessor: 'nomeCorretor',
      },
      {
        Header: 'Dias Atraso',
        id: 'diasAtraso',
        accessor: 'diasAtraso',
        sortType: 'basic',
      },
      {
        Header: 'R$ em Atraso',
        accessor: 'totalTitulos',
        sortType: 'basic',
        disableFilters: true,
        Cell: props => formatarMonetario(props.value),
      },
      {
        Header: 'Cobrador',
        accessor: 'cobrador',
        id: 'cobrador',
      },
      {
        Header: '+',
        id: 'action',
        Cell: ({ row }) => (
          <IconButton
            className="p-0"
            onClick={() =>
              mostrarDetalhesRegistro(
                registrosTabela.find(
                  re =>
                    re.cliente.codigo_conta_cliente ===
                      row.original.codigoConta &&
                    re.cliente.codigo_familia === row.original.codigoFamilia
                )
              )
            }
          >
            <FaSearch size={20} />
          </IconButton>
        ),
      },
    ],
    [registros]
  );

  const data = useMemo(
    () =>
      registrosTabela.map(registro => {
        const empreendimento = empreendimentos.find(
          emp => emp.codigo === registro.cliente.codigo_familia
        );

        const hoje = moment(new Date());
        const vencimento = moment(
          registro.cliente.data_vencimento_titulo_sinal
        );
        const duracao = moment.duration(hoje.diff(vencimento));

        const datasContato = registro.registros
          .map(reg => reg.ultimo_contato_corretor)
          .filter(data => data)
          .map(data => moment(data));

        const ultimoContatoMoment =
          datasContato.length > 0 ? moment.max(datasContato) : null;

        return {
          nome: registro.cliente.razao_social.toUpperCase(),
          cpf: registro.cliente.cpf,
          codigoConta: registro.cliente.codigo_conta_cliente,
          codigoFamilia: registro.cliente.codigo_familia,
          totalTitulos: obterTotalTitulosSinal(registro.registros),
          nomeEmpreendimento: empreendimento
            ? empreendimento.descricao
            : '(não encontrado)',
          nomeImobiliaria: registro.cliente.nome_imobiliaria || 'Revo',
          nomeCorretor: registro.cliente.apelido_conta_corretor,
          encaminhavel:
            registro.registros.filter(reg => !!reg.codigo_conta_cobrador)
              .length > 0,
          encaminhada: !!registro.registros.find(
            reg => reg.status_encaminhamento === 1
          ),
          teveContato:
            registro.registros.filter(reg => !!reg.ultimo_contato_corretor)
              .length > 0,
          diasAtraso: Math.floor(duracao.asDays()),
          ultimoContato: ultimoContatoMoment
            ? format.date(ultimoContatoMoment.toDate())
            : '-',
          cobrador: registro.cliente.apelido_conta_cobrador
            ? registro.cliente.apelido_conta_cobrador
            : 'S/ cobrador',
        };
      }),
    [registros]
  );

  const hiddenColumns = ['conta', 'familia'];

  if (!isAdm) {
    hiddenColumns.push('nomeImobiliaria');
  }

  if (isCor) {
    hiddenColumns.push('nomeCorretor');
    hiddenColumns.push('nomeImobiliaria');
  }

  const [exportandoExcel, setExportandoExcel] = useState(false);

  const exportarParaExcel = async () => {
    setExportandoExcel(true);
    try {
      const dadosExportacao = data.map(item => ({
        ultimo_contato:
          item.ultimoContato !== '-'
            ? format.datetime(item.ultimoContato)
            : 'Não informado',
        nome_cliente: item.nome,
        cpf: item.cpf,
        empreendimento: item.nomeEmpreendimento,
        imobiliaria: item.nomeImobiliaria,
        corretor: item.nomeCorretor,
        dias_atraso: item.diasAtraso,
        valor_em_atraso: formatarMonetario(item.totalTitulos),
        cobrador: item.cobrador ? item.cobrador : 'S/ cobrador',
      }));

      await salvarEmExcel(
        dadosExportacao,
        '/exportador/excel/relatorio-inadimplencia'
      );
    } catch (error) {
      console.error('Erro ao exportar para Excel:', error);
    } finally {
      setExportandoExcel(false);
    }
  };

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
        sortBy: useMemo(() => [{ id: 'nome', desc: false }], []),
        hiddenColumns: hiddenColumns,
      },
      disableSortRemove: true,
    },
    useSortBy,
    usePagination
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    pageOptions,
    page, // Instead of using 'rows', we'll use page, which has only the rows for the active page
    state: { pageIndex, pageSize },
    gotoPage,
    previousPage,
    nextPage,
    setPageSize,
    canPreviousPage,
    canNextPage,
  } = tableInstance;

  return (
    <TabelaInadimplenciaContainer className="mx-4">
      <Row>
        <Col>
          <h5>Lista de clientes inadimplentes</h5>
          <small>
            Clique em detalhes para ver as informações do cliente, enviar 2º via
            de boleto, dentre outras funções
          </small>
        </Col>
        <Col sm={2}>
          <Select
            value={{ value: pageSize, label: `Mostrar ${pageSize}` }}
            onChange={selectedOption => {
              setPageSize(Number(selectedOption.value));
            }}
            options={[10, 20, 50, 100, 200].map(size => ({
              value: size,
              label: `Mostrar ${size}`,
            }))}
            styles={customStyles}
            components={{
              DropdownIndicator,
            }}
            isSearchable={false}
          />
        </Col>
      </Row>

      <Table {...getTableProps()} striped bordered hover responsive size="sm">
        <thead>
          {// Loop over the header rows
          headerGroups.map(headerGroup => (
            // Apply the header row props
            <tr {...headerGroup.getHeaderGroupProps()}>
              {// Loop over the headers in each row
              headerGroup.headers.map(column => (
                // Apply the header cell props
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{
                    fontSize: '1rem',
                    padding: '0.75rem',
                    fontWeight: 'bold',
                  }}
                >
                  <div>
                    {// Render the header
                    column.render('Header')}
                    <span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <MdKeyboardArrowDown />
                        ) : (
                          <MdKeyboardArrowUp />
                        )
                      ) : (
                        ''
                      )}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {/* Apply the table body props */}
        <tbody {...getTableBodyProps()}>
          {// Loop over the table rows
          page.map(row => {
            // Prepare the row for display
            prepareRow(row);
            return (
              // Apply the row props
              <tr {...row.getRowProps()}>
                {// Loop over the rows cells
                row.cells.map(cell => {
                  // Apply the cell props
                  return (
                    <td
                      {...cell.getCellProps()}
                      style={{ fontSize: '0.95rem', padding: '0.75rem' }}
                    >
                      {// Render the cell contents
                      cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
      <Row className="align-items-center">
        <Col>
          <Pagination>
            <Pagination.First
              onClick={() => gotoPage(0)}
              disabled={pageIndex === 0}
            />
            <Pagination.Prev
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            />
            {pageIndex > 0 && (
              <>
                <Pagination.Item onClick={() => gotoPage(0)}>1</Pagination.Item>
                {pageIndex - 2 > 1 && <Pagination.Ellipsis />}
              </>
            )}
            {pageIndex > 2 && (
              <Pagination.Item onClick={() => gotoPage(pageIndex - 2)}>
                {pageIndex - 1}
              </Pagination.Item>
            )}

            {pageIndex > 1 && (
              <Pagination.Item onClick={() => gotoPage(pageIndex - 1)}>
                {pageIndex}
              </Pagination.Item>
            )}

            <Pagination.Item active>{pageIndex + 1}</Pagination.Item>

            {pageIndex < pageOptions.length - 2 && (
              <Pagination.Item onClick={() => gotoPage(pageIndex + 1)}>
                {pageIndex + 2}
              </Pagination.Item>
            )}
            {pageIndex < pageOptions.length - 3 && (
              <Pagination.Item onClick={() => gotoPage(pageIndex + 2)}>
                {pageIndex + 3}
              </Pagination.Item>
            )}
            {pageIndex < pageOptions.length - 1 && (
              <>
                {pageIndex + 4 < pageOptions.length && <Pagination.Ellipsis />}
                <Pagination.Item
                  onClick={() => gotoPage(pageOptions.length - 1)}
                >
                  {pageOptions.length}
                </Pagination.Item>
              </>
            )}

            <Pagination.Next
              onClick={() => nextPage()}
              disabled={!canNextPage}
            />
            <Pagination.Last onClick={() => gotoPage(pageOptions.length - 1)} />
          </Pagination>
        </Col>
        <Col xs="auto">
          <Button
            variant="success"
            onClick={exportarParaExcel}
            size="md"
            disabled={exportandoExcel}
          >
            {exportandoExcel ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="ml-2">Exportando...</span>
              </>
            ) : (
              <>
                <FaFileExcel />
                <span className="ml-2">Excel</span>
              </>
            )}
          </Button>
        </Col>
      </Row>
    </TabelaInadimplenciaContainer>
  );
};

export default TabelaInadimplencia;
